import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { not } from 'tio-ui/utilities';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import type IntlService from 'ember-intl/services/intl';
import type { Store } from '@warp-drive/core';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import { doEmployeesExistInApproverList } from '../../../utils/tuition-assistance/approvers.ts';
import {
  getSingleValueForTasField,
  getSortedOptionsByFieldName,
  type TemplateFieldName,
} from '../../../utils/tuition-assistance/fields.ts';
import Button from 'tio-ui/components/button';
import Popover from 'tio-ui/components/popover';
import { Listbox } from 'tio-ui/components/listbox';
import Modal from 'tio-ui/components/modal';
import Textarea from 'tio-ui/components/textarea';

import TooltipButton from 'tio-ui/components/tooltip-button';
import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';
import SelectTextArea from '../../tio/select-text-area.gts';
import {
  PhChatCenteredText,
  PhBuilding,
  PhFile,
  PhCalendarDots,
  PhUsers,
  PhDotsThreeVertical,
  PhArrowsCounterClockwise,
  PhArrowUUpLeft,
  PhXCircle,
} from 'ember-phosphor-icons';
import type { ProgramDrawerType } from './types.ts';

import type SessionContextService from '../../../services/session-context.ts';
import {
  transitionTasProgramInstanceRequest,
  rescindLastApproverActionRequest,
  type TasProgramInstanceTransitionEvent,
} from 'tio-data/requests/tas-program-instance';
import { buildTasActorFromSession } from 'tio-data/utils/tas-actor';
import { reloadRecord } from 'tio-data/requests/save-record';
interface ProgramApprovalFooterSignature {
  Element: HTMLDivElement;
  Args: {
    instance: TasProgramInstanceModel;
    onDrawerOpen: (drawerType: ProgramDrawerType) => void;
    activeDrawer?: ProgramDrawerType | null;
    consumingApp?: string;
  };
}

export default class ProgramApprovalFooter extends Component<ProgramApprovalFooterSignature> {
  @service declare store: Store;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;

  // Modal states
  @tracked showApproveModal = false;
  @tracked showRejectModal = false;
  @tracked showRequestChangesModal = false;
  @tracked showRescindModal = false;
  @tracked approveNote = '';
  @tracked rejectNote = '';
  @tracked requestChangeNote = '';
  @tracked hasSubmitted = false;
  @tracked submitError: string | null = null;
  @tracked showConfirmApproverOverridePrompt = false;

  get approveCommentErrors(): string[] {
    if (!this.hasSubmitted) return [];
    if (!this.approveNote.trim()) return ['Comment is required'];
    return [];
  }

  get rejectCommentErrors(): string[] {
    if (!this.hasSubmitted) return [];
    if (!this.rejectNote.trim()) return ['Comment is required'];
    return [];
  }

  get requestChangeCommentErrors(): string[] {
    if (!this.hasSubmitted) return [];
    if (!this.requestChangeNote.trim()) return ['Comment is required'];
    return [];
  }

  // ── State Detection ────────────────────────────────────────────────────

  get state() {
    return this.args.instance.state;
  }

  get currentPhase(): 'program' | 'completion' | 'withdrawal' | null {
    if (this.state.includes('PROGRAM_APPROVAL')) return 'program';
    if (this.state.includes('COMPLETION')) return 'completion';
    if (this.state.includes('WITHDRAWAL')) return 'withdrawal';
    return null;
  }

  get currentPhaseLabel() {
    switch (this.currentPhase) {
      case 'completion':
        return 'Completion';
      case 'withdrawal':
        return 'Withdrawal';
      case 'program':
      default:
        return 'Program';
    }
  }

  get inPendingApprovalState() {
    return this.state.includes('PENDING');
  }

  get canApprove() {
    return this.inPendingApprovalState;
  }

  get canRescind() {
    return this.args.instance.inRescindableState;
  }

  get canMarkComplete() {
    return this.args.instance.canMarkComplete;
  }

  // ── Adapter Method Mapping ─────────────────────────────────────────────

  /**
   * Currently-staged transition event for the modal flow (approve / reject /
   * request-changes). `_currentEvent` is set when a modal opens and cleared
   * on close so the submit handler has the event to fire.
   */
  get pendingTransitionEvent() {
    return this._currentEvent;
  }

  private _currentEvent: TasProgramInstanceTransitionEvent | undefined = undefined;

  private getEventForAction(
    action: 'approve' | 'reject' | 'requestChange'
  ): TasProgramInstanceTransitionEvent | undefined {
    const eventMap: Record<string, Record<string, TasProgramInstanceTransitionEvent>> = {
      'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL': {
        approve: 'approve-program',
        reject: 'reject-program',
        requestChange: 'decline-program',
      },
      'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL': {
        approve: 'approve-completion',
        reject: 'reject-completion',
        requestChange: 'decline-completion',
      },
      'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL': {
        approve: 'approve-withdrawal',
        reject: 'reject-withdrawal',
        requestChange: 'decline-withdrawal',
      },
    };
    return eventMap[this.state]?.[action];
  }

  // ── Approver Check ─────────────────────────────────────────────────────

  get isExpectedApprover() {
    const expectedApproverEmployeeIds = this.args.instance.currentExpectedApproverEmployeeIds;
    const employees = this.sessionContext.user?.person?.employees || [];
    return doEmployeesExistInApproverList(employees, expectedApproverEmployeeIds);
  }

  // ── Drawer Active State ────────────────────────────────────────────────

  isDrawerActive = (drawerType: ProgramDrawerType) => {
    return this.args.activeDrawer === drawerType;
  };

  handleDrawerClick = (drawerType: ProgramDrawerType) => {
    this.args.onDrawerOpen(drawerType);
  };

  handleMoreActionsMenu = (key: string) => {
    switch (key) {
      case 'approvalQueue':
        this.handleDrawerClick('approval-queue');
        break;
      case 'requestChange':
        this.openRequestChangesModal();
        break;
      case 'reject':
        this.openRejectModal();
        break;
      case 'rescind':
        this.showRescindModal = true;
        break;
    }
  };

  get disabledMenuKeys(): string[] {
    const disabled: string[] = [];
    if (!this.inPendingApprovalState) {
      disabled.push('requestChange');
      disabled.push('reject');
    }
    if (!this.canRescind) {
      disabled.push('rescind');
    }
    return disabled;
  }

  // ── Approval Flow ──────────────────────────────────────────────────────

  openApproveModal = () => {
    this._currentEvent = this.getEventForAction('approve');
    this.approveNote = this.templateApproveComment;
    this.showApproveModal = true;
  };

  closeApproveModal = () => {
    this.showApproveModal = false;
    this.approveNote = '';
    this._currentEvent = undefined;
    this.hasSubmitted = false;
    this.submitError = null;
  };

  attemptSubmitApproval = () => {
    this.hasSubmitted = true;
    if (!this.approveNote.trim()) return;
    if (!this.isExpectedApprover) {
      this.showConfirmApproverOverridePrompt = true;
    } else {
      this.submitApprovalTask.perform();
    }
  };

  submitApprovalTask = dropTask(async () => {
    this.showConfirmApproverOverridePrompt = false;

    if (!this._currentEvent || !this.approveNote.trim()) return;

    try {
      this.submitError = null;
      await this.store.request(
        transitionTasProgramInstanceRequest(
          this.args.instance.id as string,
          this._currentEvent,
          buildTasActorFromSession(this.sessionContext),
          this.approveNote
        )
      );
      this.closeApproveModal();
      this.notifications.add(this.intl.t('tuition_assistance.program_details.action_success'), {
        appearance: 'success',
      });
      await reloadRecord(this.store, this.args.instance);
    } catch (e) {
      console.error(e);
      this.submitError = e instanceof Error ? e.message : String(e);
    }
  });

  // ── More Actions ───────────────────────────────────────────────────────

  openRejectModal = () => {
    this._currentEvent = this.getEventForAction('reject');
    this.rejectNote = this.templateRejectComment;
    this.showRejectModal = true;
  };

  closeRejectModal = () => {
    this.showRejectModal = false;
    this.rejectNote = '';
    this._currentEvent = undefined;
    this.hasSubmitted = false;
    this.submitError = null;
  };

  openRequestChangesModal = () => {
    this._currentEvent = this.getEventForAction('requestChange');
    this.requestChangeNote = this.templateRequestChangeComment;
    this.showRequestChangesModal = true;
  };

  closeRequestChangesModal = () => {
    this.showRequestChangesModal = false;
    this.requestChangeNote = '';
    this._currentEvent = undefined;
    this.hasSubmitted = false;
    this.submitError = null;
  };

  submitRejectTask = dropTask(async () => {
    if (!this._currentEvent || !this.rejectNote.trim()) return;

    try {
      this.submitError = null;
      await this.store.request(
        transitionTasProgramInstanceRequest(
          this.args.instance.id as string,
          this._currentEvent,
          buildTasActorFromSession(this.sessionContext),
          this.rejectNote
        )
      );
      this.closeRejectModal();
      this.notifications.add(this.intl.t('tuition_assistance.program_details.action_success'), {
        appearance: 'success',
      });
      await reloadRecord(this.store, this.args.instance);
    } catch (e) {
      console.error(e);
      this.submitError = e instanceof Error ? e.message : String(e);
    }
  });

  attemptSubmitReject = () => {
    this.hasSubmitted = true;
    if (!this.rejectNote.trim()) return;
    this.submitRejectTask.perform();
  };

  submitRequestChangesTask = dropTask(async () => {
    if (!this._currentEvent || !this.requestChangeNote.trim()) return;

    try {
      this.submitError = null;
      await this.store.request(
        transitionTasProgramInstanceRequest(
          this.args.instance.id as string,
          this._currentEvent,
          buildTasActorFromSession(this.sessionContext),
          this.requestChangeNote
        )
      );
      this.closeRequestChangesModal();
      this.notifications.add(this.intl.t('tuition_assistance.program_details.action_success'), {
        appearance: 'success',
      });
      await reloadRecord(this.store, this.args.instance);
    } catch (e) {
      console.error(e);
      this.submitError = e instanceof Error ? e.message : String(e);
    }
  });

  attemptSubmitRequestChanges = () => {
    this.hasSubmitted = true;
    if (!this.requestChangeNote.trim()) return;
    this.submitRequestChangesTask.perform();
  };

  // ── Rescind ────────────────────────────────────────────────────────────

  rescindTask = dropTask(async () => {
    try {
      await this.store.request(rescindLastApproverActionRequest(this.args.instance.id as string));
      this.showRescindModal = false;
      await reloadRecord(this.store, this.args.instance);
    } catch (e) {
      console.error(e);
    }
  });

  // ── Canned Response Notes ──────────────────────────────────────────────

  private getFieldNameForAction(
    action: 'approve' | 'reject' | 'requestChange'
  ): TemplateFieldName | null {
    const fieldNameMap: Record<string, Record<string, TemplateFieldName>> = {
      'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL': {
        approve: 'PROGRAM_APPROVAL_APPROVE_TEXT' as TemplateFieldName,
        reject: 'PROGRAM_APPROVAL_REJECT_TEXT' as TemplateFieldName,
        requestChange: 'PROGRAM_APPROVAL_REVISE_TEXT' as TemplateFieldName,
      },
      'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL': {
        approve: 'WITHDRAW_COMPLETE_APPROVAL_APPROVE_TEXT' as TemplateFieldName,
        reject: 'WITHDRAW_COMPLETE_APPROVAL_REJECT_TEXT' as TemplateFieldName,
        requestChange: 'WITHDRAW_COMPLETE_APPROVAL_REVISE_TEXT' as TemplateFieldName,
      },
      'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL': {
        approve: 'WITHDRAW_COMPLETE_APPROVAL_APPROVE_TEXT' as TemplateFieldName,
        reject: 'WITHDRAW_COMPLETE_APPROVAL_REJECT_TEXT' as TemplateFieldName,
        requestChange: 'WITHDRAW_COMPLETE_APPROVAL_REVISE_TEXT' as TemplateFieldName,
      },
    };
    return fieldNameMap[this.state]?.[action] ?? null;
  }

  private getTagForAction(action: 'approve' | 'reject' | 'requestChange') {
    const tagMap: Record<string, Record<string, string>> = {
      'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL': {
        approve: 'TAS.ProgramApproval.APPROVE',
        reject: 'TAS.ProgramApproval.REJECT',
        requestChange: 'TAS.ProgramApproval.REVISE',
      },
    };
    return tagMap[this.state]?.[action] ?? null;
  }

  private companyCommentsForTag(tag: string): { title: string; body: string }[] {
    const partnerComments = this.args.instance.employee?.company?.comments ?? [];
    return partnerComments
      .filter((comment) => comment.tags?.includes(tag))
      .map((comment) => comment.tasApprovalNote);
  }

  private getResponseOptions(action: 'approve' | 'reject' | 'requestChange') {
    const fieldName = this.getFieldNameForAction(action);
    if (!fieldName) return [];
    const tasFieldOptions = this.args.instance.tasProgramTemplate?.tasFieldOptions ?? [];
    return getSortedOptionsByFieldName(fieldName, tasFieldOptions);
  }

  private getTextAreaOptions(action: 'approve' | 'reject' | 'requestChange') {
    const templateOptions = this.getResponseOptions(action);
    const tag = this.getTagForAction(action);
    const companyOptions = tag ? this.companyCommentsForTag(tag) : [];
    const allOptions = [
      ...templateOptions.map((option) => ({ title: option.label, body: option.value })),
      ...companyOptions,
    ];

    if (allOptions.length > 1) return allOptions;
    return [];
  }

  private getTemplateComment(action: 'approve' | 'reject' | 'requestChange') {
    const templateOptions = this.getResponseOptions(action);
    const tag = this.getTagForAction(action);
    const companyOptions = tag ? this.companyCommentsForTag(tag) : [];
    const allOptions = [
      ...templateOptions.map((o) => ({ title: o.label, body: o.value })),
      ...companyOptions,
    ];

    if (allOptions.length === 1) {
      return allOptions[0]?.body ?? '';
    }

    const fields = this.args.instance.tasProgramTemplate?.fields;
    if (!fields) return '';
    const fieldName = this.getFieldNameForAction(action);
    if (!fieldName) return '';
    const value = getSingleValueForTasField(fieldName, fields);
    return typeof value === 'string' ? value : '';
  }

  get approveTextAreaOptions() {
    return this.getTextAreaOptions('approve');
  }

  get rejectTextAreaOptions() {
    return this.getTextAreaOptions('reject');
  }

  get requestChangeTextAreaOptions() {
    return this.getTextAreaOptions('requestChange');
  }

  get templateApproveComment() {
    return this.getTemplateComment('approve');
  }

  get templateRejectComment() {
    return this.getTemplateComment('reject');
  }

  get templateRequestChangeComment() {
    return this.getTemplateComment('requestChange');
  }

  <template>
    <div
      class="sticky bottom-0 left-0 right-0 tio-bg-surface border-t tio-border-default shadow-lg rounded-b-lg"
      data-test-program-approval-footer
      ...attributes
    >
      <div class="mx-auto px-2 py-2">
        <div class="tio-col md:flex-row justify-between items-stretch md:items-center">
          {{! Primary Approve Button }}
          <Button
            @intent="primary"
            @size="sm"
            @isDisabled={{not this.canApprove}}
            class="md:w-auto w-full"
            {{on "click" this.openApproveModal}}
            data-test-approve-button
          >
            <span class="hidden md:inline">{{t "tuition_assistance.application_details.approve"}}
              {{this.currentPhaseLabel}}</span>
            <span class="md:hidden">{{t "tuition_assistance.application_details.approve"}}</span>
          </Button>

          {{! Icon Buttons Row }}
          <div class="tio-row-extra-tight justify-around md:justify-end md:gap-1">
            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.comments"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "comments"}}
              data-test-button="footer-comments-button"
              {{on "click" (fn this.handleDrawerClick "comments")}}
            >
              <PhChatCenteredText @size="20" class="md:hidden" />
              <PhChatCenteredText @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.partner_information"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "partner-info"}}
              data-test-button="footer-partner-info-button"
              {{on "click" (fn this.handleDrawerClick "partner-info")}}
            >
              <PhBuilding @size="20" class="md:hidden" />
              <PhBuilding @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.attachments"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "attachments"}}
              data-test-button="footer-attachments-button"
              {{on "click" (fn this.handleDrawerClick "attachments")}}
            >
              <PhFile @size="20" class="md:hidden" />
              <PhFile @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.timeline"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "timeline"}}
              data-test-button="footer-timeline-button"
              {{on "click" (fn this.handleDrawerClick "timeline")}}
            >
              <PhCalendarDots @size="20" class="md:hidden" />
              <PhCalendarDots @size="24" class="hidden md:block" />
            </TooltipButton>

            {{! More Actions Popover }}
            <Popover @placement="top-end">
              <:trigger as |pop|>
                <TooltipButton
                  @tooltip={{t "tuition_assistance.application_approval.more_actions"}}
                  @tooltipPlacement="top"
                  @appearance="minimal"
                  @intent="default"
                  @size="sm"
                  class="!p-2 !rounded-md hidden md:flex"
                  {{pop.register}}
                  {{on "click" pop.toggle}}
                >
                  <PhDotsThreeVertical @size="24" />
                </TooltipButton>
              </:trigger>
              <:body>
                <Listbox
                  @intent="primary"
                  @onAction={{this.handleMoreActionsMenu}}
                  @disabledKeys={{this.disabledMenuKeys}}
                  as |l|
                >
                  <l.Item @key="approvalQueue">
                    <span class="tio-row-tight">
                      <PhUsers @size="16" />
                      {{t "tuition_assistance.application_approval.approval_queue"}}
                    </span>
                  </l.Item>

                  <l.Separator />

                  <l.Item @key="requestChange">
                    <span class="tio-row-tight">
                      <PhArrowsCounterClockwise @size="16" />
                      {{t "tuition_assistance.application_details.request_change"}}
                    </span>
                  </l.Item>

                  <l.Item @key="rescind">
                    <span class="tio-row-tight">
                      <PhArrowUUpLeft @size="16" />
                      {{t "tuition_assistance.application_approval.undo_last"}}
                    </span>
                  </l.Item>

                  <l.Separator />

                  <l.Item @key="reject" @intent="danger">
                    <span class="tio-row-tight">
                      <PhXCircle @size="16" />
                      {{t "tuition_assistance.application_details.reject"}}
                    </span>
                  </l.Item>
                </Listbox>
              </:body>
            </Popover>
          </div>
        </div>
      </div>
    </div>

    {{! ── Approve Modal ────────────────────────────────────────────────── }}
    <Modal @isOpen={{this.showApproveModal}} @onClose={{this.closeApproveModal}}>
      <:header>{{t "tuition_assistance.application_details.approve"}}
        {{this.currentPhaseLabel}}</:header>
      <:body>
        <div class="space-y-4">
          <div data-test-approve-note>
            {{#if this.approveTextAreaOptions.length}}
              <SelectTextArea
                @options={{this.approveTextAreaOptions}}
                @optionLabel="title"
                @optionValue="body"
                @value={{this.approveNote}}
                @onChange={{fn (mut this.approveNote)}}
                @errors={{this.approveCommentErrors}}
                @placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
              />
            {{else}}
              <Textarea
                @value={{this.approveNote}}
                @onChange={{fn (mut this.approveNote)}}
                @errors={{this.approveCommentErrors}}
                placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
              />
            {{/if}}
          </div>

          {{#if this.submitError}}
            <p class="text-sm tio-fg-danger">{{this.submitError}}</p>
          {{/if}}
        </div>
      </:body>
      <:footer>
        <div class="tio-flex-tight justify-end">
          <Button
            @appearance="outlined"
            data-test-approve-cancel-button
            {{on "click" this.closeApproveModal}}
          >
            {{t "common.cancel"}}
          </Button>
          <Button
            @intent="primary"
            @isRunning={{this.submitApprovalTask.isRunning}}
            data-test-approve-submit-button
            {{on "click" this.attemptSubmitApproval}}
          >
            {{t "tuition_assistance.application_details.approve"}}
          </Button>
        </div>
      </:footer>
    </Modal>

    {{! ── Reject Modal ─────────────────────────────────────────────────── }}
    <Modal @isOpen={{this.showRejectModal}} @onClose={{this.closeRejectModal}}>
      <:header>{{t "tuition_assistance.application_details.reject"}}
        {{this.currentPhaseLabel}}</:header>
      <:body>
        <div class="space-y-4">
          <div data-test-reject-note>
            {{#if this.rejectTextAreaOptions.length}}
              <SelectTextArea
                @options={{this.rejectTextAreaOptions}}
                @optionLabel="title"
                @optionValue="body"
                @value={{this.rejectNote}}
                @onChange={{fn (mut this.rejectNote)}}
                @errors={{this.rejectCommentErrors}}
                @placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
              />
            {{else}}
              <Textarea
                @value={{this.rejectNote}}
                @onChange={{fn (mut this.rejectNote)}}
                @errors={{this.rejectCommentErrors}}
                placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
              />
            {{/if}}
          </div>
          {{#if this.submitError}}
            <p class="text-sm tio-fg-danger">{{this.submitError}}</p>
          {{/if}}
        </div>
      </:body>
      <:footer>
        <div class="tio-flex-tight justify-end">
          <Button
            @appearance="outlined"
            data-test-reject-cancel-button
            {{on "click" this.closeRejectModal}}
          >
            {{t "common.cancel"}}
          </Button>
          <Button
            @intent="danger"
            @isRunning={{this.submitRejectTask.isRunning}}
            data-test-reject-submit-button
            {{on "click" this.attemptSubmitReject}}
          >
            {{t "tuition_assistance.application_details.reject"}}
          </Button>
        </div>
      </:footer>
    </Modal>

    {{! ── Request Changes Modal ────────────────────────────────────────── }}
    <Modal @isOpen={{this.showRequestChangesModal}} @onClose={{this.closeRequestChangesModal}}>
      <:header>{{t "tuition_assistance.application_details.request_change"}}</:header>
      <:body>
        <div class="space-y-4">
          <div data-test-request-change-note>
            {{#if this.requestChangeTextAreaOptions.length}}
              <SelectTextArea
                @options={{this.requestChangeTextAreaOptions}}
                @optionLabel="title"
                @optionValue="body"
                @value={{this.requestChangeNote}}
                @onChange={{fn (mut this.requestChangeNote)}}
                @errors={{this.requestChangeCommentErrors}}
                @placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
              />
            {{else}}
              <Textarea
                @value={{this.requestChangeNote}}
                @onChange={{fn (mut this.requestChangeNote)}}
                @errors={{this.requestChangeCommentErrors}}
                placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
              />
            {{/if}}
          </div>
          {{#if this.submitError}}
            <p class="text-sm tio-fg-danger">{{this.submitError}}</p>
          {{/if}}
        </div>
      </:body>
      <:footer>
        <div class="tio-flex-tight justify-end">
          <Button
            @appearance="outlined"
            data-test-request-change-cancel-button
            {{on "click" this.closeRequestChangesModal}}
          >
            {{t "common.cancel"}}
          </Button>
          <Button
            @intent="warning"
            @isRunning={{this.submitRequestChangesTask.isRunning}}
            data-test-request-change-submit-button
            {{on "click" this.attemptSubmitRequestChanges}}
          >
            {{t "tuition_assistance.application_details.request_change"}}
          </Button>
        </div>
      </:footer>
    </Modal>

    {{! ── Rescind Confirmation ─────────────────────────────────────────── }}
    <ConfirmationDialog
      @isOpen={{this.showRescindModal}}
      @title={{t "tuition_assistance.application_details.rescind.confirm_button"}}
      @intent="warning"
      @onConfirm={{this.rescindTask.perform}}
      @onCancel={{fn (mut this.showRescindModal) false}}
      @isRunning={{this.rescindTask.isRunning}}
      @confirmText={{t "tuition_assistance.application_details.rescind.confirm_button"}}
      @cancelText={{t "common.cancel"}}
    >
      <p class="mb-2 font-semibold">
        {{t "tuition_assistance.application_details.rescind.body"}}
      </p>
    </ConfirmationDialog>

    {{! ── Approver Override Confirmation ───────────────────────────────── }}
    <ConfirmationDialog
      @isOpen={{this.showConfirmApproverOverridePrompt}}
      @title="Out of Sequence Approver"
      @intent="warning"
      @onConfirm={{this.submitApprovalTask.perform}}
      @onCancel={{fn (mut this.showConfirmApproverOverridePrompt) false}}
      @isRunning={{this.submitApprovalTask.isRunning}}
      @confirmText={{t "common.yes"}}
      @cancelText={{t "common.no"}}
    >
      <p class="mb-2 font-semibold">
        {{t "tuition_assistance.application_details.out_of_sequence_approver_warning"}}
      </p>
    </ConfirmationDialog>
  </template>
}
